
import { Component, Mixins, Prop } from "vue-property-decorator"

import ValidatorMixin from "@/shared/mixins/validator/ValidatorMixin"

import PaymentMode from "@/shared/enums/PaymentMode"
import SubscriptionScheme from '@/shared/enums/SubscriptionScheme'

import { JSON } from "../utils/types"

import CheckoutForm from "../models/CheckoutForm"
import { COLOMBIA_CITIES } from '../utils/colombia'
import EInput from "./Input.vue"
import EPhoneInput from "./PhoneInput.vue"
import ESelect from "./Select.vue"
import ETaxIdInput from "./TaxIdInput.vue"

@Component({
  components: {
    EInput,
    EPhoneInput,
    ESelect,
    ETaxIdInput,
  },
})
export default class PersonalInfoForm extends Mixins(ValidatorMixin) {
  @Prop() readonly data!: CheckoutForm

  @Prop({ type: Boolean, default: false }) readonly use3ds!: boolean

  private person = this.data.person;

  private address = this.data.address;

  private payment = this.data.payment;

  private readonly cities = COLOMBIA_CITIES;

  get shouldShowTaxIdField(): boolean {
    const method = this.payment.method

    if (!method) {
      return false
    }

    return [PaymentMode.bankSlip, PaymentMode.pix, PaymentMode.picpay].includes(method) ||
      (method === PaymentMode.bankTransfer && ['CO', 'CL', 'PE'].includes(this.payment.country))
  }

  get shouldShowTaxIdTypeField(): boolean {
    return this.payment.method === PaymentMode.bankTransfer && ['CO', 'CL', 'PE'].includes(this.payment.country)
  }

  get shouldShowPersonTypeField(): boolean {
    return this.payment.method === PaymentMode.bankTransfer && ['CO'].includes(this.payment.country)
  }

  get shouldShowCityField(): boolean {
    return this.payment.method === PaymentMode.bankTransfer && this.payment.country === 'CO'
  }

  get taxIdTypes(): string[] {
    switch (this.payment.country) {
      case 'MX': return ['CC', 'PP', 'CURP', 'RFC']
      case 'CO': return ['CC', 'NIT', 'CE', 'TI', 'PP']
      case 'CL': return ['CC', 'PP', 'RUT']
      case 'PE': return ['CE', 'PAS', 'RUC', 'DNI']
      case 'EC': return ['PAS', 'RUC', 'CI']
      case 'CR': return ['CI', 'DIMEX']
      default: return []
    }
  }

  get personTypes() {
    return [
      { label: this.$t('message.naturalPerson'), value: 'NATURAL_PERSON' },
      { label: this.$t('message.company'), value: 'COMPANY' },
    ]
  }

  get shouldShowEmailField(): boolean {
    return this.payment.method !== PaymentMode.picpay
  }

  get shouldShowFirstNameField(): boolean {
    return this.payment.method !== PaymentMode.picpay &&
      (this.payment.method !== PaymentMode.bankTransfer || this.payment.country == 'CO') &&
      this.payment.method !== PaymentMode.pagoEfectivo &&
      this.payment.method !== PaymentMode.deuna &&
      this.payment.method !== PaymentMode.nequi &&
      this.payment.method !== PaymentMode.mach &&
      this.payment.method !== PaymentMode.qrInteroperable &&
      this.payment.method !== PaymentMode.breb
  }

  get shouldShowSurnameField(): boolean {
    return this.shouldShowFirstNameField
  }

  get shouldShowBirthDateFieldViaPaymentCountry(): boolean {
    return this.payment.country === 'BR'
  }

  get shouldShowBirthDateField() {
    return !this.use3ds && !this.person.disableDob && this.shouldShowBirthDateFieldViaPaymentCountry
  }

  get shouldShowPhoneField(): boolean {
    return (this.payment.country === 'BR' && this.payment.method !== PaymentMode.picpay && this.payment.method !== PaymentMode.bankSlip) ||
      (this.payment.country === 'CO' && this.payment.method === PaymentMode.bankTransfer) ||
      (this.payment.country !== 'BR' && (this.isCardSubscription || this.payment.method === PaymentMode.creditCard || this.payment.method === PaymentMode.debitCard))
  }

  private phoneChanged(value: JSON) {
    this.person.phoneCountry = value.phoneCountry
    this.person.phoneArea = value.phoneArea
    this.person.phone = value.phoneNumber
  }

  mounted() {
    if (this.person.taxIdType && !this.taxIdTypes.includes(this.person.taxIdType)) {
      this.person.taxIdType = undefined
    }
  }

  get isCardSubscription(): boolean {
    return this.isSubscriptionOf(SubscriptionScheme.card)
  }

  private isSubscriptionOf(scheme: SubscriptionScheme): boolean {
    const { subscription } = this.data

    if (!subscription) {
      return false;
    }

    return subscription.scheme === scheme;
  }
}
